import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

import AuthContext from '../../context/authContext';
import useLoyaltyProgram from '../../hooks/useLoyaltyProgram';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

const PRIZE_TYPE_LABELS = {
  valueVoucher: 'Wertgutschein (€)',
  discountCode: 'Rabattcode (%)',
  upsell: 'Zusatzartikel gratis',
  resourceVoucherProduct: 'Produktgutschein',
  none: 'Niete',
};

const emptyPrize = () => ({
  label: 'Neues Feld',
  type: 'none',
  weight: 1,
  active: true,
  validityDays: 365,
});

function LoyaltyProgramManagement() {
  const authCtx = useContext(AuthContext);
  const { getProgram, getStatistics, updateProgram } = useLoyaltyProgram();

  const [draft, setDraft] = useState(null);
  const [options, setOptions] = useState({ entertainments: [], upgrades: [], resourceVoucherProducts: [] });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const hasRight = authCtx.checkRight('loyalty:manage');

  const load = useCallback(async () => {
    try {
      const [program, statistics] = await Promise.all([getProgram(), getStatistics()]);
      if (program) {
        setDraft(structuredClone(program.program));
        setOptions(program.options || options);
      }
      if (statistics) setStats(statistics);
    } catch (err) {
      toast.error('Fehler beim Laden des Treueprogramms');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (hasRight) load();
  }, [hasRight, load]);

  // Weights are relative and never add up to anything, which makes them easy to edit and
  // impossible to read. Whoever sets "weight 3" is deciding a probability, so the probability is
  // what the table shows.
  const odds = useMemo(() => {
    const prizes = draft?.wheel?.prizes || [];
    const playable = prizes.filter(p => p.active && Number(p.weight) > 0);
    const total = playable.reduce((sum, p) => sum + Number(p.weight), 0);
    return prize => {
      if (!prize.active || Number(prize.weight) <= 0 || total <= 0) return null;
      return (Number(prize.weight) / total) * 100;
    };
  }, [draft]);

  if (!hasRight) {
    return <Navigate to='/welcome' replace />;
  }

  const patch = mutate => {
    setDraft(current => {
      if (!current) return current;
      const next = structuredClone(current);
      mutate(next);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await updateProgram({ stampCard: draft.stampCard, wheel: draft.wheel });
      toast.success('Treueprogramm gespeichert');
    } catch (err) {
      toast.error('Fehler beim Speichern des Treueprogramms');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !draft) {
    return (
      <Box sx={{ ...viewRootTextPrimarySx, p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  const countingCount =
    draft.stampCard.countingEntertainmentIds.length || options.entertainments.length;
  const playablePrizes = (draft.wheel.prizes || []).filter(p => p.active && Number(p.weight) > 0);

  return (
    <Box sx={{ ...viewRootTextPrimarySx, p: 3 }}>
      <Stack direction='row' justifyContent='space-between' alignItems='center' sx={{ mb: 2 }}>
        <Typography variant='h5'>Treueprogramm</Typography>
        <Stack direction='row' spacing={1} alignItems='center'>
          {stats && (
            <>
              <Chip size='small' label={`${stats.accounts} Konten`} />
              <Chip size='small' label={`${stats.cardsComplete} volle Karten`} />
              <Chip size='small' label={`${stats.spins} Drehungen`} />
            </>
          )}
          <Button variant='contained' onClick={save} disabled={saving}>
            {saving ? 'Speichert …' : 'Speichern'}
          </Button>
        </Stack>
      </Stack>

      <Typography variant='body2' sx={{ mb: 3, opacity: 0.75 }}>
        Stempelkarte und Glücksrad des Kundenkontos. Hier wird festgelegt, welche Erlebniswelten
        zählen, was eine volle Karte einbringt und welche Felder das Rad mit welcher
        Wahrscheinlichkeit ausspielt.
      </Typography>

      {/* ------------------------------------------------------------------- Stempelkarte */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction='row' justifyContent='space-between' alignItems='center' sx={{ mb: 2 }}>
            <Typography variant='h6'>Stempelkarte</Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={draft.stampCard.enabled}
                  onChange={event => patch(next => { next.stampCard.enabled = event.target.checked; })}
                />
              }
              label='Aktiv'
            />
          </Stack>

          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label='Überschrift'
                fullWidth
                defaultValue={draft.stampCard.title}
                onBlur={event => patch(next => { next.stampCard.title = event.target.value; })}
              />
              <TextField
                label='Beschreibung'
                fullWidth
                multiline
                minRows={2}
                defaultValue={draft.stampCard.description}
                onBlur={event => patch(next => { next.stampCard.description = event.target.value; })}
              />
            </Stack>

            <Box>
              <Typography variant='subtitle2'>Zählende Erlebniswelten</Typography>
              <Typography variant='body2' sx={{ opacity: 0.75, mb: 1 }}>
                Nichts ausgewählt heißt <strong>alle</strong> — eine neue Erlebniswelt kommt dann
                automatisch auf die Karte. Aktuell zählen {countingCount}.
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' }, gap: 0.5 }}>
                {options.entertainments.map(entertainment => (
                  <FormControlLabel
                    key={entertainment._id}
                    control={
                      <Checkbox
                        checked={draft.stampCard.countingEntertainmentIds.includes(entertainment._id)}
                        onChange={event =>
                          patch(next => {
                            const ids = new Set(next.stampCard.countingEntertainmentIds);
                            if (event.target.checked) ids.add(entertainment._id);
                            else ids.delete(entertainment._id);
                            next.stampCard.countingEntertainmentIds = [...ids];
                          })
                        }
                      />
                    }
                    label={entertainment.longName || entertainment.entertainmentName}
                  />
                ))}
              </Box>
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label='Gutschein (€)'
                type='number'
                defaultValue={draft.stampCard.rewardVoucherValue}
                onBlur={event =>
                  patch(next => { next.stampCard.rewardVoucherValue = Number(event.target.value); })
                }
              />
              <TextField
                label='Gültig (Tage)'
                type='number'
                defaultValue={draft.stampCard.rewardVoucherValidityDays}
                onBlur={event =>
                  patch(next => {
                    next.stampCard.rewardVoucherValidityDays = Number(event.target.value);
                  })
                }
              />
              <TextField
                label='Dauerrabatt (%)'
                type='number'
                defaultValue={draft.stampCard.rewardDiscountPercent}
                onBlur={event =>
                  patch(next => { next.stampCard.rewardDiscountPercent = Number(event.target.value); })
                }
              />
            </Stack>

            <Alert severity='info'>
              <AlertTitle>Der Dauerrabatt läuft nicht ab</AlertTitle>
              Er wird als persönlicher Rabattcode ausgestellt, der an die E-Mail-Adresse des Kunden
              gebunden ist und nur mit ihr eingelöst werden kann. Eine volle Karte wird genau
              einmal je Kunde ausgezahlt.
            </Alert>
          </Stack>
        </CardContent>
      </Card>

      {/* --------------------------------------------------------------------------- Rad */}
      <Card>
        <CardContent>
          <Stack direction='row' justifyContent='space-between' alignItems='center' sx={{ mb: 2 }}>
            <Typography variant='h6'>Glücksrad</Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={draft.wheel.enabled}
                  onChange={event => patch(next => { next.wheel.enabled = event.target.checked; })}
                />
              }
              label='Aktiv'
            />
          </Stack>

          <Stack spacing={3}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label='Überschrift'
                defaultValue={draft.wheel.title}
                onBlur={event => patch(next => { next.wheel.title = event.target.value; })}
              />
              <TextField
                label='Beschreibung'
                fullWidth
                defaultValue={draft.wheel.description}
                onBlur={event => patch(next => { next.wheel.description = event.target.value; })}
              />
            </Stack>

            <Box>
              <Stack direction='row' justifyContent='space-between' alignItems='center'>
                <Typography variant='subtitle2'>Dreh-Intervall</Typography>
                <Button
                  size='small'
                  startIcon={<AddIcon />}
                  onClick={() =>
                    patch(next => {
                      next.wheel.spinTiers = [
                        ...next.wheel.spinTiers,
                        { label: 'Neue Stufe', minVisitedEntertainments: 0, cooldownHours: 168 },
                      ];
                    })
                  }
                >
                  Stufe
                </Button>
              </Stack>
              <Typography variant='body2' sx={{ opacity: 0.75, mb: 1 }}>
                Es gilt die höchste passende Stufe. Ohne Stufen greift die Vorgabe.
              </Typography>
              <TextField
                label='Vorgabe (Stunden)'
                type='number'
                sx={{ mb: 1 }}
                defaultValue={draft.wheel.defaultCooldownHours}
                onBlur={event =>
                  patch(next => { next.wheel.defaultCooldownHours = Number(event.target.value); })
                }
              />
              {draft.wheel.spinTiers.length > 0 && (
                <Table size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell>Bezeichnung</TableCell>
                      <TableCell>ab … besuchten Welten</TableCell>
                      <TableCell>Intervall (Std.)</TableCell>
                      <TableCell width={60} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {draft.wheel.spinTiers.map((tier, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <TextField
                            size='small'
                            defaultValue={tier.label}
                            onBlur={event =>
                              patch(next => { next.wheel.spinTiers[index].label = event.target.value; })
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size='small'
                            type='number'
                            defaultValue={tier.minVisitedEntertainments}
                            onBlur={event =>
                              patch(next => {
                                next.wheel.spinTiers[index].minVisitedEntertainments = Number(
                                  event.target.value
                                );
                              })
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size='small'
                            type='number'
                            defaultValue={tier.cooldownHours}
                            onBlur={event =>
                              patch(next => {
                                next.wheel.spinTiers[index].cooldownHours = Number(event.target.value);
                              })
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size='small'
                            aria-label='Stufe entfernen'
                            onClick={() => patch(next => { next.wheel.spinTiers.splice(index, 1); })}
                          >
                            <DeleteIcon fontSize='small' />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>

            <Divider />

            <Box>
              <Stack direction='row' justifyContent='space-between' alignItems='center'>
                <Typography variant='subtitle2'>Gewinnfelder</Typography>
                <Button
                  size='small'
                  startIcon={<AddIcon />}
                  onClick={() => patch(next => { next.wheel.prizes = [...next.wheel.prizes, emptyPrize()]; })}
                >
                  Feld
                </Button>
              </Stack>
              <Typography variant='body2' sx={{ opacity: 0.75, mb: 1 }}>
                Die Gewichte sind relativ und müssen sich zu nichts addieren — die Spalte „Chance"
                rechnet daraus die tatsächliche Wahrscheinlichkeit. Ein Feld ohne Kontingent kann
                unbegrenzt oft gewonnen werden.
              </Typography>

              <Box sx={{ overflowX: 'auto' }}>
                <Table size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell>Bezeichnung</TableCell>
                      <TableCell>Art</TableCell>
                      <TableCell>Wert</TableCell>
                      <TableCell>Gewicht</TableCell>
                      <TableCell>Chance</TableCell>
                      <TableCell>Kontingent</TableCell>
                      <TableCell>Aktiv</TableCell>
                      <TableCell width={60} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {draft.wheel.prizes.map((prize, index) => {
                      const chance = odds(prize);
                      return (
                        <TableRow key={prize._id || index}>
                          <TableCell>
                            <TextField
                              size='small'
                              // Wide enough for a real prize name — the default width cut
                              // "Nächstes Mal" off after ten characters.
                              sx={{ minWidth: 200 }}
                              defaultValue={prize.label}
                              onBlur={event =>
                                patch(next => { next.wheel.prizes[index].label = event.target.value; })
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              size='small'
                              select
                              sx={{ minWidth: 180 }}
                              value={prize.type}
                              onChange={event =>
                                patch(next => { next.wheel.prizes[index].type = event.target.value; })
                              }
                            >
                              {Object.keys(PRIZE_TYPE_LABELS).map(type => (
                                <MenuItem key={type} value={type}>
                                  {PRIZE_TYPE_LABELS[type]}
                                </MenuItem>
                              ))}
                            </TextField>
                          </TableCell>
                          <TableCell>
                            {prize.type === 'valueVoucher' && (
                              <TextField
                                size='small'
                                type='number'
                                label='Betrag'
                                defaultValue={prize.voucherValue ?? 0}
                                onBlur={event =>
                                  patch(next => {
                                    next.wheel.prizes[index].voucherValue = Number(event.target.value);
                                  })
                                }
                              />
                            )}
                            {prize.type === 'discountCode' && (
                              <TextField
                                size='small'
                                type='number'
                                label='Prozent'
                                defaultValue={prize.discountPercent ?? 0}
                                onBlur={event =>
                                  patch(next => {
                                    next.wheel.prizes[index].discountPercent = Number(event.target.value);
                                  })
                                }
                              />
                            )}
                            {prize.type === 'upsell' && (
                              <TextField
                                size='small'
                                select
                                sx={{ minWidth: 180 }}
                                label='Artikel'
                                value={prize.upgradeId || ''}
                                onChange={event =>
                                  patch(next => { next.wheel.prizes[index].upgradeId = event.target.value; })
                                }
                              >
                                {options.upgrades.map(upgrade => (
                                  <MenuItem key={upgrade._id} value={upgrade._id}>
                                    {upgrade.name}
                                  </MenuItem>
                                ))}
                              </TextField>
                            )}
                            {prize.type === 'resourceVoucherProduct' && (
                              <TextField
                                size='small'
                                select
                                sx={{ minWidth: 180 }}
                                label='Produkt'
                                value={prize.resourceVoucherProductId || ''}
                                onChange={event =>
                                  patch(next => {
                                    next.wheel.prizes[index].resourceVoucherProductId = event.target.value;
                                  })
                                }
                              >
                                {options.resourceVoucherProducts.map(product => (
                                  <MenuItem key={product._id} value={product._id}>
                                    {product.name}
                                  </MenuItem>
                                ))}
                              </TextField>
                            )}
                            {prize.type === 'none' && <span>—</span>}
                          </TableCell>
                          <TableCell>
                            <TextField
                              size='small'
                              type='number'
                              inputProps={{ step: 0.1, 'aria-label': 'Gewicht' }}
                              sx={{ width: 90 }}
                              defaultValue={prize.weight}
                              onBlur={event =>
                                patch(next => { next.wheel.prizes[index].weight = Number(event.target.value); })
                              }
                            />
                          </TableCell>
                          <TableCell>{chance === null ? '—' : `${chance.toFixed(1)} %`}</TableCell>
                          <TableCell>
                            <TextField
                              size='small'
                              type='number'
                              placeholder='∞'
                              sx={{ width: 100 }}
                              inputProps={{ 'aria-label': 'Kontingent' }}
                              defaultValue={prize.totalLimit ?? ''}
                              onBlur={event =>
                                patch(next => {
                                  const value = event.target.value.trim();
                                  next.wheel.prizes[index].totalLimit = value ? Number(value) : null;
                                })
                              }
                            />
                            {!!prize.wonCount && (
                              <Typography variant='caption' sx={{ display: 'block', opacity: 0.7 }}>
                                {prize.wonCount} vergeben
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Switch
                              checked={prize.active}
                              inputProps={{ 'aria-label': 'Feld aktiv' }}
                              onChange={event =>
                                patch(next => { next.wheel.prizes[index].active = event.target.checked; })
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <IconButton
                              size='small'
                              aria-label='Feld entfernen'
                              onClick={() => patch(next => { next.wheel.prizes.splice(index, 1); })}
                            >
                              <DeleteIcon fontSize='small' />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>

              {playablePrizes.length === 0 && (
                <Alert severity='error' sx={{ mt: 2 }}>
                  <AlertTitle>Kein spielbares Feld</AlertTitle>
                  Solange kein aktives Feld mit einem Gewicht über 0 existiert, lässt sich das Rad
                  nicht drehen — auch wenn es aktiv ist.
                </Alert>
              )}
            </Box>

            {stats && stats.prizeBreakdown.length > 0 && (
              <Box>
                <Typography variant='subtitle2' sx={{ mb: 1 }}>
                  Bisher ausgespielt
                </Typography>
                <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
                  {stats.prizeBreakdown.map(entry => (
                    <Chip
                      key={`${entry.label}-${entry.type}`}
                      size='small'
                      variant='outlined'
                      label={`${entry.label}: ${entry.count}`}
                    />
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}

export default LoyaltyProgramManagement;
